import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GET_BADGES, BADGES_IMAGE_URL, GET_USER_RANK_BY_MONTH, MEMBERSHIP, CLAIM_BADGE, GET_USER_BADGES } from "../helpers/Constant";
import { getDataWithToken, postDataWithToken } from '../api/api';
import { useTranslation } from 'react-i18next';
import Loader from '../common/loader';
import BackButton from '../common/backButton';
import { MdOutlineArrowBackIos, MdOutlineArrowForwardIos } from 'react-icons/md';
import moment from 'moment';
import { getUserInfo } from '../utils/common';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { useNavigate } from 'react-router-dom';

function RealBadge() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [badgeData, setBadgeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userRankByMonth, setUserRankByMonth] = useState([]);
    const [userBadges, setUserBadges] = useState([]);
    const userInfo = getUserInfo();
    const [isPremium] = useState(userInfo?.userAccount?.isPrimeUser);
    const [currentSlide, setCurrentSlide] = useState(0);
    const splideRef = useRef(null);

    const options = {
        pagination: true,
        arrows: true,
        rewind: false,
        autoplay: false,
        speed: 500,
        perPage: 1,
        perMove: 1,
    };

    const getNumberWithSuffix = (number) => {
        if(number === 1) return 'st';
        if(number === 2) return 'nd';
        if(number === 3) return 'rd';
        return 'th';
    }

    const fetchAllBadgeData = useCallback(async () => {
        try {
            const response = await getDataWithToken(GET_BADGES);
            if(response) {
                response.sort((a, b) => a.orderNumber - b.orderNumber);

                const monthlyBadges = response.filter((badge) => badge.targetType === 'monthly');
                setBadgeData(monthlyBadges);
            } else {
                setBadgeData([]);
            }
        } catch (error) {
            setBadgeData([]);
        }
    }, []);

    const fetchUserRankByMonth = useCallback(async () => {
        try {
            const response = await getDataWithToken(GET_USER_RANK_BY_MONTH);
            if(response.length > 0) {
                const userRankByMonth = [];
                response.forEach(item => {
                    if(item.users[0].rank > 0) {
                        userRankByMonth.push({
                            month: item.month,
                            monthFull: moment(item.month+"-"+item.year, "MM-YYYY").format('MMMM'),
                            rank: item.users[0].rank,
                            rankSuffix: getNumberWithSuffix(item.users[0].rank),
                        });
                    }
                });
                setUserRankByMonth(userRankByMonth);
            }
        } catch (error) {}
    }, []);

    const fetchUserBadges = useCallback(async () => {
        try {
            const response = await getDataWithToken(GET_USER_BADGES);
            if(response.length > 0) {
                setUserBadges(response);
            }
        } catch (error) {
            setUserBadges([]);
        }
    }, []);

    useEffect(() => {
        async function fetchBadgeData() {
            try {
                await fetchAllBadgeData();

                await fetchUserRankByMonth();

                await fetchUserBadges();
                
            } catch (error) {
                setLoading(false);
            } finally {
                setLoading(false);
            }
        }
        fetchBadgeData()
    }, [fetchUserRankByMonth, fetchUserBadges]);

    useEffect(() => {
        const splide = splideRef.current?.splide;
        if (!splide) return;

        const updateIndex = () => setCurrentSlide(splide.index);

        splide.on('move', updateIndex);
        updateIndex(); // Initialize once on mount

        return () => splide.off('move', updateIndex);
    }, []);

    const generateArray = (length) => {
        return Array.from({ length }, (_, index) => index);
    };

    const handleToGetPremium = () => {
        navigate(`/${MEMBERSHIP}`);
    }

    const handleClaimBadge = async () => {
        const response = await postDataWithToken(CLAIM_BADGE, { badgeId: badgeData[currentSlide]._id });
        if(response.status === 200) {
            await fetchUserBadges();
        }
    }

    const currentYear = new Date().getFullYear();

    return (
        <>
            <div className='wrapper h-100 d-flex flex-column p-0'>
                {loading && <Loader />}

                <div className='pt-3 ps-3 position-absolute top-0 start-0 z-1'>
                    <BackButton label={t('Home')} />
                </div>

                <div className={`real_badge_single d-flex flex-column flex-fill ${isPremium ? '' : 'non-premium'}`}>
                    <Splide
                        ref={splideRef}
                        options={options}
                        renderControls={() => (
                            <div className="splide__arrows">
                                <button type="button" className="splide__arrow splide__arrow--prev" aria-label="Previous slide">
                                    <MdOutlineArrowBackIos />
                                </button>
                                <button type="button" className="splide__arrow splide__arrow--next" aria-label="Next slide">
                                    <MdOutlineArrowForwardIos />
                                </button>
                            </div>
                        )}
                    >
                        {badgeData.map((badge, index) => (
                            <SplideSlide key={index}>
                                <div className={`real_badge_item target-${badge.target} d-flex flex-column h-100 text-center`}>
                                    <h2 className='text-white text-uppercase fw-bold text-center mb-0'>{currentYear} {t('season')}</h2>
                                    <div className='real_badge_image_wrapper position-relative'>
                                        <div className='real_badge_image'>
                                            <img src={BADGES_IMAGE_URL + badge.image} alt={badge.title} className='mx-auto position-relative z-1' />
                                            <div className='real_badge_image_shadow bg-white rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        </div>
                                        <div className='real_badge_round_wrapper position-absolute top-50 start-50 translate-middle'>
                                            <div className='real_badge_round real_badge_round_1 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                            <div className='real_badge_round real_badge_round_2 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                            <div className='real_badge_round real_badge_round_3 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                            <div className='real_badge_round real_badge_round_4 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        </div>
                                    </div>
                                    <h2 className='real_badge_title my-1 fw-bold'>{badge.title}</h2>
                                    <div className='real_badge_sub_title'><span className='fw-medium'>Real Badge Achievement</span></div>

                                    <div className='real_badge_description fw-light mt-4' dangerouslySetInnerHTML={{ __html: badge.description }} />
                                    <div className='real_badge_achievements mb-3 d-flex flex-wrap justify-content-center'>
                                        {generateArray(parseInt(badge.target) || 3).map((item, index) => (
                                            <div key={index} className='real_badge_achievement_item p-1 position-relative'>
                                                {userRankByMonth[index]?.rank ? (
                                                <div className='real_badge_achievement_inner position-relative h-100 d-flex flex-column justify-content-center'>
                                                        <div className='real_badge_date fw-bold'>{userRankByMonth[index]?.rank}<sup>{userRankByMonth[index]?.rankSuffix}</sup></div>
                                                        <div className='real_badge_month fw-light'>{userRankByMonth[index]?.monthFull}</div>
                                                    </div>
                                                ) : (
                                                    <div className='real_badge_achievement_lock h-100 d-flex flex-column justify-content-center align-items-center'>
                                                        <img src="../images/lock-icon.svg" alt="Lock" />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className={`real_badge_feedback fw-light ${!isPremium || (isPremium && badge.target <= userRankByMonth.length) ? 'd-none' : ''}`}>Stay in the Top 100 for {badge.target} months to unlock.</div>
                                    <div className={`real_badge_feedback fw-light achieved ${isPremium && badge.target <= userRankByMonth.length ? '' : 'd-none'}`}>You proved your consistency Titan status achieved.</div>
                                    <div className={`real_badge_feedback fw-light non-premium ${!isPremium ? '' : 'd-none'}`}>Get Premium to Unlock the Real Badges</div>
                                </div>
                            </SplideSlide>
                        ))}
                    </Splide>

                    <div className='real_badge_btn text-center'>
                        {isPremium && userBadges.findIndex(badge => badge.badges.toString() === badgeData[currentSlide]?._id.toString()) === -1 ? (  
                            <button
                                className='cta_button bg-warning text-dark fw-bold'
                                disabled={badgeData[currentSlide]?.target > userRankByMonth.length}
                                onClick={handleClaimBadge}
                            >
                                <span className='text-dark'>Claim Your Badge</span>
                            </button>
                        ) : isPremium && userBadges.findIndex(badge => badge.badges.toString() === badgeData[currentSlide]?._id.toString()) !== -1 ? (
                            <button className='cta_button bg-warning text-dark fw-bold'>
                                <span className='text-dark'>Already Claimed</span>
                            </button>
                        ) : (
                            <button className='cta_button bg-warning text-dark fw-bold' onClick={handleToGetPremium}>
                                <span className='text-dark'>Get Premium</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
export default RealBadge